import { Component, OnInit } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  SituViewWebApiClient,
  GetBuilderThemeRepresentationRequest,
  GetBuilderThemeRepresentationResponse,
  GetBuilderPartneredManufacturersRequest,
  PartneredManufacturer,
  ProductBucketSummary,
  ProductRepresentationRow,
  ThemeRepresentationRow,
  ManufacturerExposureRow,
  RepresentationBucket,
} from 'situview-webapi-client';
import { AuthService, StateService } from 'src/app/services';
import { SituBaseComponent } from 'src/app/shared/situ-base-component';

/**
 * Situ-admin builder-side product representation report. Pick a builder (header company selector),
 * pick one of its partnered manufacturers (or all), and see how that manufacturer's products are
 * represented across the builder's themes, bucketed by how recently those themes were viewed.
 *
 * Port of the ad-hoc BuilderThemeRepresentation report onto the SituView endpoints.
 * NOTE: LastViewedUtc is currently incomplete (see the caveat banner) — this is a data-coverage
 * mirror, not yet an engagement measure.
 */
@Component({
  selector: 'builder-representation-report',
  templateUrl: './builder-representation-report.component.html',
  styleUrls: ['./builder-representation-report.component.scss'],
})
export class BuilderRepresentationReportComponent extends SituBaseComponent implements OnInit {
  // Expose the enum to the template.
  Bucket = RepresentationBucket;

  // Manufacturer dropdown. `undefined` value = "All partnered".
  manufacturers: PartneredManufacturer[] = [];
  selectedManufacturerId: string | undefined;

  activeDays = 30;

  report: GetBuilderThemeRepresentationResponse | undefined;

  loadingManufacturers = false;
  loadingReport = false;

  readonly productColumns = ['manufacturer', 'product', 'sku', 'themeCount', 'themes', 'lastViewed', 'bucket'];
  readonly themeColumns = ['theme', 'lastViewed', 'bucket', 'products', 'manufacturers', 'represented'];
  readonly exposureColumns = ['manufacturer', 'themes', 'activeThemes', 'products', 'lastSeen'];

  constructor(
    authService: AuthService,
    stateService: StateService,
    private situViewClient: SituViewWebApiClient,
    private snackBar: MatSnackBar
  ) {
    super(authService, stateService);
  }

  ngOnInit(): void {
    super.ngOnInit();
    if (this.selectedCompany?.id) {
      this.loadManufacturers();
    }
  }

  protected onSelectedCompanyChange(): void {
    // Builder changed in the header — reset and reload the partner list.
    this.selectedManufacturerId = undefined;
    this.report = undefined;
    this.manufacturers = [];
    if (this.selectedCompany?.id) {
      this.loadManufacturers();
    }
  }

  private loadManufacturers(): void {
    const builderId = this.selectedCompany?.id;
    if (!builderId) {
      return;
    }
    this.loadingManufacturers = true;
    this.situViewClient
      .getBuilderPartneredManufacturers(new GetBuilderPartneredManufacturersRequest({ builderCompanyId: builderId }))
      .then(response => {
        if (response.success) {
          this.manufacturers = response.manufacturers ?? [];
        } else {
          this.manufacturers = [];
          this.showMessage(response.errorMessages?.join(' ') || 'Unable to load partnered manufacturers.');
        }
      })
      .catch(() => {
        this.manufacturers = [];
        this.showMessage('Unable to load partnered manufacturers. Please try again.');
      })
      .finally(() => (this.loadingManufacturers = false));
  }

  onManufacturerChange(): void {
    this.loadReport();
  }

  loadReport(): void {
    const builderId = this.selectedCompany?.id;
    if (!builderId) {
      return;
    }
    this.loadingReport = true;
    this.report = undefined;

    const request = new GetBuilderThemeRepresentationRequest({
      builderCompanyId: builderId,
      manufacturerCompanyId: this.selectedManufacturerId,
      manufacturersOnly: true,
      activeDays: this.activeDays,
    });

    this.situViewClient
      .getBuilderThemeRepresentation(request)
      .then(response => {
        if (response.success) {
          this.report = response;
        } else {
          this.showMessage(response.errorMessages?.join(' ') || 'Unable to load the report.');
        }
      })
      .catch(() => this.showMessage('Unable to load the report. Please try again.'))
      .finally(() => (this.loadingReport = false));
  }

  // ---- template helpers ----

  productBucketCount(bucket: RepresentationBucket): number {
    return this.report?.productsByBucket?.find((b: ProductBucketSummary) => b.bucket === bucket)?.products ?? 0;
  }

  productBucketPct(bucket: RepresentationBucket): number {
    return this.report?.productsByBucket?.find((b: ProductBucketSummary) => b.bucket === bucket)?.pct ?? 0;
  }

  bucketLabel(bucket: RepresentationBucket): string {
    switch (bucket) {
      case RepresentationBucket.Active: return 'Active';
      case RepresentationBucket.Stale: return 'Stale';
      case RepresentationBucket.Never: return 'Never viewed';
      case RepresentationBucket.NotInTheme: return 'Not in a theme';
      default: return '';
    }
  }

  bucketClass(bucket: RepresentationBucket): string {
    switch (bucket) {
      case RepresentationBucket.Active: return 'bucket-active';
      case RepresentationBucket.Stale: return 'bucket-stale';
      case RepresentationBucket.Never: return 'bucket-never';
      case RepresentationBucket.NotInTheme: return 'bucket-none';
      default: return '';
    }
  }

  get productRows(): ProductRepresentationRow[] {
    return this.report?.products ?? [];
  }

  get themeRows(): ThemeRepresentationRow[] {
    return this.report?.themes ?? [];
  }

  get exposureRows(): ManufacturerExposureRow[] {
    return this.report?.manufacturerExposure ?? [];
  }

  private showMessage(message: string): void {
    this.snackBar.open(message, 'OK', { duration: 4000 });
  }
}
